import type { ReactNode } from 'react';

function cx(...parts: (string | undefined | false)[]): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Сообщения в стиле страницы «Запись на занятие» (граница + лёгкий тинт + круг с маркером):
 * `banner` — как блок ошибки при отправке формы; `field` — компактно под полями.
 * `variant` меняет набор «акцентных» цветов: ошибка / подсказка (info) / успех.
 */
export type EmbedMessageVariant = 'error' | 'info' | 'success';

export type EmbedMessageSize = 'field' | 'banner';

const VARIANT = {
  error: {
    border: 'border-[color-mix(in_srgb,var(--error-text)_32%,var(--border))]',
    bg: 'bg-[color-mix(in_srgb,var(--error-text)_5%,var(--bg-soft))]',
    iconRing: 'border-[color-mix(in_srgb,var(--error-text)_45%,var(--border))]',
    icon: 'text-[var(--error-text)]',
    label: '!'
  },
  info: {
    border: 'border-[color-mix(in_srgb,var(--accent)_32%,var(--border))]',
    bg: 'bg-[color-mix(in_srgb,var(--accent)_7%,var(--bg-soft))]',
    iconRing: 'border-[color-mix(in_srgb,var(--accent)_42%,var(--border))]',
    icon: 'text-[var(--accent)]',
    label: 'i'
  },
  success: {
    border: 'border-[color-mix(in_srgb,rgb(22_163_74)_32%,var(--border))]',
    bg: 'bg-[color-mix(in_srgb,rgb(22_163_74)_6%,var(--bg-soft))]',
    iconRing: 'border-[color-mix(in_srgb,rgb(22_163_74)_40%,var(--border))]',
    icon: 'text-emerald-700 dark:text-emerald-300',
    label: '✓'
  }
} as const;

export type EmbedMessageBlockProps = {
  variant: EmbedMessageVariant;
  size?: EmbedMessageSize;
  children: ReactNode;
  className?: string;
  id?: string;
  role?: 'alert' | 'status';
  'aria-live'?: 'assertive' | 'polite' | 'off';
  /** Скрыть круг с подписью (только текст в рамке). */
  showIcon?: boolean;
};

export function EmbedMessageBlock({
  variant,
  size = 'field',
  children,
  className,
  id,
  role = 'alert',
  'aria-live': ariaLive,
  showIcon = true
}: EmbedMessageBlockProps) {
  const t = VARIANT[variant];
  const isBanner = size === 'banner';
  const live = ariaLive ?? (isBanner && variant === 'error' ? 'assertive' : 'polite');

  return (
    <div
      id={id}
      className={cx(
        'flex rounded-lg',
        t.border,
        t.bg,
        isBanner ? 'gap-3 p-3.5 text-sm leading-relaxed' : 'gap-2 p-2.5 text-[13px] leading-snug',
        className
      )}
      role={role}
      aria-live={live}
    >
      {showIcon ? (
        <span
          className={cx(
            'flex shrink-0 items-center justify-center rounded-full border-2 bg-[var(--bg-elevated)] font-extrabold',
            t.iconRing,
            t.icon,
            isBanner ? 'h-7 w-7 text-sm' : 'h-5 w-5 min-h-[1.25rem] min-w-[1.25rem] text-[11px]'
          )}
          aria-hidden
        >
          {t.label}
        </span>
      ) : null}
      <p
        className={cx(
          'min-w-0 flex-1 whitespace-pre-wrap',
          isBanner ? 'text-[15px] text-[var(--text)]' : 'text-[13px] text-[var(--text)]'
        )}
      >
        {children}
      </p>
    </div>
  );
}

/** Кольцо у поля при ошибке валидации (согласовано с SessionPage). */
export const embedFieldErrorInputClass = 'ring-1 ring-[var(--error-text)]';
